<template>
  <v-dialog
    :value="value"
    @input="emit('input', $event)"
    persistent
    max-width="640"
  >
    <v-card class="pa-4">
      <v-card-title class="justify-center pt-0">
        <span>Schedule email report sharing</span>
        <v-spacer></v-spacer>
        <v-btn icon absolute top right @click="closeEmailDialog">
          <v-icon>mdi-window-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-form ref="form" v-model="isDialogValid">
        <v-card-text>
          <v-container>
            <v-row no-gutters>
              <v-col sm="6">
                <date-field
                  class="date-field"
                  v-model="scheduledReport.startDate"
                  label="Select Send Date"
                  :min-date="now"
                  :rules="[rules.required('Send Date')]"
                  narrow
                >
                </date-field>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col sm="6">
                <v-select
                  dense
                  filled
                  v-model="scheduledReport.frequency"
                  :items="frequencyOptions"
                  item-text="label"
                  item-value="value"
                  :rules="[rules.required('Repeat Schedule')]"
                >
                </v-select>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col sm="8">
                <v-text-field
                  label="Client/Company"
                  v-model="scheduledReport.companyName"
                  filled
                  dense
                  :rules="[rules.required('Client or Company Name')]"
                >
                </v-text-field>
              </v-col>
            </v-row>
            <div
              v-for="(recipient, index) in scheduledReport.recipients"
              :key="index"
            >
              <v-row no-gutters>
                <v-col sm="8">
                  <v-text-field
                    label="Contact Name"
                    v-model="recipient.contactName"
                    filled
                    dense
                    :rules="[rules.required('Contact Name')]"
                  >
                  </v-text-field>
                </v-col>
                <v-col>
                  <div class="remove-recipient-button-container">
                    <v-btn
                      v-if="index > 0"
                      text
                      height="16px"
                      width="16px"
                      :title="
                        `Remove ${recipient.contactName ||
                          'empty contact'} from ${scheduledReport.companyName ||
                          'a'} shared report`
                      "
                      @click="scheduledReport.recipients.splice(index, 1)"
                    >
                      <v-icon>$boldx</v-icon>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col sm="10">
                  <v-text-field
                    filled
                    dense
                    v-model="recipient.email"
                    label="Email"
                    :rules="[rules.required('Email'), rules.email]"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
            <v-btn text @click="addRecipient" class="add-recipient-btn">
              <span>
                + Add Another Contact/Email
              </span>
            </v-btn>
          </v-container>
        </v-card-text>
      </v-form>
      <v-card-actions>
        <v-spacer></v-spacer>
        <cta-button @click="saveScheduleDispatch" :disabled="!isDialogValid">
          Schedule Email
        </cta-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { scheduledReportAPI } from "@/services/api";
import { datePickerNow, datePickerToDisplay } from "@/services/util";
import { post, put } from "@/services/request";
import BlueCallToAction from "@/components/providertrust/buttons/BlueCallToAction";
import * as rules from "@/services/rules";
import DateField from "@/components/DateField";
import hasNotifications from "@/mixins/has-notifications";

const emptyRecipient = () => {
  return {
    companyName: null,
    contactName: null,
    email: null
  };
};

const emptyScheduledReport = () => {
  return {
    startDate: null,
    frequency: null,
    type: "VENDOR_COMPLIANCE_REPORT",
    companyName: null,
    recipients: [emptyRecipient()]
  };
};

const buildSuccessMessage = scheduledReport => {
  const startDate = scheduledReport.startDate;
  const emails = scheduledReport.recipients
    .map(r => r.email)
    .join(", ")
    .replace(/,([^,]*)$/, ", and$1");
  const companyName = scheduledReport.recipients[0].companyName;

  return `Your report has been scheduled to send via email on ${datePickerToDisplay(
    startDate
  )} to ${emails} of ${companyName}`;
};

export default {
  name: "ComplianceReport",
  props: ["value"],
  data: () => ({
    rules,
    isDialogValid: false,
    frequencyOptions: [
      { label: "One Time", value: "ONE_TIME" },
      { label: "Monthly", value: "MONTHLY" }
    ],
    scheduledReport: emptyScheduledReport(),
    now: datePickerNow()
  }),
  components: {
    "cta-button": BlueCallToAction,
    DateField
  },
  mixins: [hasNotifications],
  methods: {
    setReport(report) {
      this.scheduledReport = report;
      this.scheduledReport.companyName = report.recipients.length
        ? report.recipients[0].companyName
        : null;
    },
    saveScheduleDispatch() {
      this.saveSchedule(this.editMode ? put : post);
    },
    saveSchedule(action) {
      //company name is for the scheduled report as a whole, but it is actually an attribute of recipient
      //  so i copy it here from the ui format to api format
      this.scheduledReport.recipients.forEach(
        r => (r.companyName = this.scheduledReport.companyName)
      );

      const successMessage = buildSuccessMessage(this.scheduledReport);

      action(scheduledReportAPI, this.scheduledReport)
        .then(() => {
          this.showMessage(successMessage);
          this.scheduledReport = emptyScheduledReport();
          this.$emit("added");
        })
        .catch(() => {
          this.showMessage({
            mode: "error",
            text: "Unable to schedule report."
          });
        })
        .finally(() => {
          this.$refs.form.reset();
          this.$emit("input", false);
        });
    },
    addRecipient() {
      this.scheduledReport.recipients.push(emptyRecipient());
    },
    closeEmailDialog() {
      this.scheduledReport = emptyScheduledReport();
      this.$refs.form.reset();
      this.$emit("input", false);
    }
  },
  computed: {
    editMode() {
      return this.scheduledReport.id != null;
    }
  }
};
</script>

<style lang="sass" scoped>
.v-dialog .v-text-field--filled.v-input--dense.v-text-field--single-line
  ::v-deep .v-input__control > .v-input__slot
    min-height: 52px !important
  ::v-deep i
    margin-top: 12px

.v-dialog .v-text-field.v-text-field--enclosed
  ::v-deep .v-text-field__details
    margin-bottom: 0px

.v-card__actions
  justify-content: right

.v-btn:not(.v-btn--round).v-size--default.add-recipient-btn
  padding: 0
  margin-top: -32px
  color: $primary
  font-weight: bold
  font-size: 16px
  line-height: 20px

.remove-recipient-button-container
  display: flex
  justify-content: left
  align-items: center
  height: 100%
  padding-bottom: 16px
  padding-left: 8px
  .v-btn:not(.v-btn--round).v-size--default
    min-width: 0px
</style>
